

import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';

import {
  AGTestCommand,
  AGTestCommandResultFeedback,
  CustomScoringError,
  ExpectedReturnCode,
  FeedbackCategory,
  ResultOutput,
  Submission,
  ValueFeedbackLevel
} from "ag-client-typescript";

import { GlobalData } from '@/app.vue';
import Diff from '@/components/diff.vue';
import DescriptionRenderer from "@/components/project_view/submission_detail/description_renderer.vue"
import InfoBlurb from '@/components/InfoBlurb.vue';
import ViewFile from "@/components/view_file/view_file.vue";
import { handle_global_errors_async } from '@/error_handling';
import { Created } from '@/lifecycle';


@Component({
  components: {
    DescriptionRenderer,
    Diff,
    InfoBlurb,
    ViewFile
  }
})
export default class AGTestCommandResultDetail extends Vue implements Created{
  @Inject({from: 'globals'})
  globals!: GlobalData;
  d_globals = this.globals;

  @Prop({required: true, type: Submission})
  submission!: Submission;

  @Prop({required: true, type: Object})
  ag_test_command_result!: AGTestCommandResultFeedback;

  @Prop({required: true, type: String})
  fdbk_category!: FeedbackCategory;

  d_staff_description: string | null = null;

  d_output_size: ResultOutput.AGTestCommandResultOutputSize | null = null;

  d_stdout_content: Promise<string> | null = null;
  d_stderr_content: Promise<string> | null = null;
  d_stdout_load_progress: number | null = null;
  d_stderr_load_progress: number | null = null;

  d_stdout_diff: Promise<string[]> | null = null;
  d_stderr_diff: Promise<string[]> | null = null;
  d_stdout_diff_load_progress: number | null = null;
  d_stderr_diff_load_progress: number | null = null;

  @Watch('fdbk_category')
  on_fdbk_category_change(new_value: FeedbackCategory, old_value: FeedbackCategory) {
    return this.get_output();
  }

  readonly ExpectedReturnCode = ExpectedReturnCode;
  readonly ValueFeedbackLevel = ValueFeedbackLevel;
  readonly CustomScoringError = CustomScoringError;

  async created() {
    if (this.d_globals.user_roles.is_staff) {
      let ag_test_command = await AGTestCommand.get_by_pk(
        this.ag_test_command_result.ag_test_command_pk);
      this.d_staff_description = ag_test_command.staff_description;
    }
    return this.get_output();
  }

  get only_custom_scoring_points_available() {
    if (!this.ag_test_command_result.custom_scoring_used) {
      return false;
    }

    return (this.ag_test_command_result.return_code_points_possible === 0
      && this.ag_test_command_result.stdout_points_possible === 0
      && this.ag_test_command_result.stderr_points_possible === 0
    )
  }

  get custom_scoring_label() {
    if (this.ag_test_command_result.custom_scoring_label !== null) {
      return this.ag_test_command_result.custom_scoring_label;
    }
    else if (this.only_custom_scoring_points_available) {
      return 'Points';
    }
    else {
      return 'Instructor score adjustment'
    }
  }

  get custom_scoring_error_msg() {
    switch (this.ag_test_command_result.custom_scoring_error) {
      case CustomScoringError.none:
        return null;
      case CustomScoringError.exceeded_max_points:
        return ('The custom score that was parsed from the output exceeded the max custom scoring'
                + ' points possible. The max custom scoring points possible were awarded instead of'
                + ' the parsed value');
      case CustomScoringError.failed_to_find_pattern:
        return 'Failed to find a custom score matching the configured pattern in output';
      case CustomScoringError.non_integer:
        return 'The parsed custom score value was a non-integer. Only integer values are supported';
      default: {
        const _exhaustiveCheck: never = this.ag_test_command_result.custom_scoring_error;
        return _exhaustiveCheck;
      }
    }
  }

  get show_correctness_fieldset() {
    return this.ag_test_command_result.return_code_correct !== null
           || (this.ag_test_command_result.timed_out !== null
               && this.ag_test_command_result.timed_out!)
           || this.ag_test_command_result.actual_return_code !== null
           || this.ag_test_command_result.stdout_correct !== null
           || this.ag_test_command_result.stderr_correct !== null
           || this.ag_test_command_result.custom_scoring_used;
  }

  @handle_global_errors_async
  async get_output() {
    this.d_stdout_content = null;
    this.d_stderr_content = null;
    this.d_stdout_diff = null;
    this.d_stderr_diff = null;

    this.d_output_size = await ResultOutput.get_ag_test_cmd_result_output_size(
      this.submission.pk,
      this.ag_test_command_result.pk,
      this.fdbk_category
    );
    this.load_stdout_diff();
    this.load_stderr_diff();
    this.load_stdout_content();
    this.load_stderr_content();
  }

  load_stdout_content() {
    if (this.d_output_size!.stdout_size === null || this.d_output_size!.stdout_size === 0) {
      return;
    }

    this.d_stdout_load_progress = null;
    this.d_stdout_content = ResultOutput.get_ag_test_cmd_result_stdout(
      this.submission.pk,
      this.ag_test_command_result.pk,
      this.fdbk_category,
      (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.d_stdout_load_progress = 100 * (1.0 * event.loaded / event.total);
        }
      }
    );
  }

  load_stderr_content() {
    if (this.d_output_size!.stderr_size === null || this.d_output_size!.stderr_size === 0) {
      return;
    }

    this.d_stderr_load_progress = null;
    this.d_stderr_content = ResultOutput.get_ag_test_cmd_result_stderr(
      this.submission.pk,
      this.ag_test_command_result.pk,
      this.fdbk_category,
      (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.d_stderr_load_progress = 100 * (1.0 * event.loaded / event.total);
        }
      }
    );
  }

  load_stdout_diff() {
    if (this.d_output_size!.stdout_diff_size === null
        || this.d_output_size!.stdout_diff_size === 0) {
      return;
    }

    this.d_stdout_diff_load_progress = null;
    this.d_stdout_diff = ResultOutput.get_ag_test_cmd_result_stdout_diff(
      this.submission.pk,
      this.ag_test_command_result.pk,
      this.fdbk_category,
      (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.d_stdout_diff_load_progress = 100 * (1.0 * event.loaded / event.total);
        }
      }
    );
  }

  load_stderr_diff() {
    if (this.d_output_size!.stderr_diff_size === null
        || this.d_output_size!.stderr_diff_size === 0) {
      return;
    }

    this.d_stderr_diff_load_progress = null;
    this.d_stderr_diff = ResultOutput.get_ag_test_cmd_result_stderr_diff(
      this.submission.pk,
      this.ag_test_command_result.pk,
      this.fdbk_category,
      (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.d_stderr_diff_load_progress = 100 * (1.0 * event.loaded / event.total);
        }
      }
    );
  }
}
